import type { Validations, Violation } from "@credit-acceptance/step6-bff-client";

import { stripFieldPrefix, type TranslatedViolations, translateViolations } from "../../../serverViolations";
import type { InsuranceInformationInput } from "./InsuranceInformation.schema";

// ---------------------------------------------------------------------------
// Insurance.violations
//
// Insurance findings ride on `vehicle.validations`, scoped by an `insurance.`
// prefix (confirmed for `insurance.insurerName`). Both the read-model
// (VehicleInsurance) and write-model (VehicleInsuranceSaveRequestV2) names are
// accepted. The `vehicleInsurance.` prefix is accepted but unconfirmed.
// ---------------------------------------------------------------------------

type InsurancePath = keyof InsuranceInformationInput;

const INSURANCE_PREFIXES = ["insurance", "vehicleInsurance"];

const INSURANCE_FIELD_MAP: Record<string, InsurancePath> = {
  insurerName: "insurerName",
  insurerPhone: "insurerPhone",
  agentName: "agentName",
  agent: "agentName",
  expirationDate: "expirationDate",
  effectiveDate: "expirationDate",
  comprehensiveDeductibleAmount: "comprehensiveDeductibleAmount",
  compDeductible: "comprehensiveDeductibleAmount",
  collisionDeductibleAmount: "collisionDeductibleAmount",
  collisionDeductible: "collisionDeductibleAmount",
  isCaLossPayee: "isCaLossPayee",
  selectedCALossPayee: "isCaLossPayee",
  fullCoverage: "fullCoverage",
};

/**
 * @param {Violation} violation - A `vehicle.validations` entry.
 * @returns {boolean} Whether it belongs to the Insurance subsection rather than Contract Vehicle.
 */
export function isInsuranceViolation(violation: Violation): boolean {
  const head = violation.field?.split(".")[0].toLowerCase();
  return INSURANCE_PREFIXES.some((prefix) => prefix.toLowerCase() === head);
}

/**
 * Translates the insurance findings in `vehicle.validations`; everything else is left to Contract Vehicle.
 * @param {Validations | undefined} validations - `vehicle.validations` from the dashboard or a save response.
 * @param {{ fullCoverageVisible: boolean }} context - Rendering context.
 * @param {boolean} context.fullCoverageVisible - Whether Full Coverage renders (it is hidden for COLON).
 * @returns {TranslatedViolations<InsurancePath>} Field errors plus unmapped insurance findings.
 */
export function translateInsuranceViolations(
  validations: Validations | undefined,
  context: { fullCoverageVisible: boolean }
): TranslatedViolations<InsurancePath> {
  return translateViolations(
    {
      requiredViolations: validations?.requiredViolations?.filter(isInsuranceViolation),
      businessRuleViolations: validations?.businessRuleViolations?.filter(isInsuranceViolation),
    },
    (violation) => {
      const path = INSURANCE_FIELD_MAP[stripFieldPrefix(violation.field ?? "", INSURANCE_PREFIXES)];
      return path === "fullCoverage" && !context.fullCoverageVisible ? undefined : path;
    }
  );
}
